/**
 * Hook unifié pour gérer les permissions des utilisateurs
 * Centralise toute la logique de vérification des permissions
 */

import { useState, useEffect, useCallback } from 'react';
import { User } from 'firebase/auth';
import { UserAffiliationRepository } from '@/services/firebase/user-affiliation-repository';
import { UserAffiliation } from '@/models/userAffiliation';

interface UsePermissionsReturn {
  // État
  isLoading: boolean;
  initialized: boolean; // Flag pour savoir si le premier chargement est terminé
  error: string | null;

  // Vérifications de permissions (rôles)
  isAdminForOrganization: (organizationId: string) => boolean;
  hasAccessToOrganization: (organizationId: string) => boolean;
  getUserRole: (organizationId: string) => UserAffiliation['role'] | null;

  // Vérifications de permissions granulaires
  canViewOrders: (organizationId: string) => boolean;
  canManageOrders: (organizationId: string) => boolean;
  canManageConfiguration: (organizationId: string) => boolean;
  canViewAnalytics: (organizationId: string) => boolean;
  canManageUsers: (organizationId: string) => boolean;

  // Données
  userAffiliations: UserAffiliation[];
  refreshPermissions: () => Promise<void>;
}

export const usePermissions = (
  currentUser: User | null,
  customerId: string | null,
  isSuperAdmin: boolean = false
): UsePermissionsReturn => {
  const [userAffiliations, setUserAffiliations] = useState<UserAffiliation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * S'abonne aux affiliations de l'utilisateur en temps réel
   */
  useEffect(() => {
    if (!customerId || !currentUser) {
      setUserAffiliations([]);
      setIsLoading(false);
      setInitialized(true);
      return;
    }

    setIsLoading(true);
    setError(null);

    console.log(`📡 usePermissions - Établissement du listener d'affiliations pour customerId: ${customerId}`);

    // Utiliser le listener temps réel
    const unsubscribe = UserAffiliationRepository.subscribeByCustomerId(
      customerId,
      (affiliations) => {
        console.log(`✅ usePermissions - Listener: ${affiliations.length} affiliations reçues`);
        setUserAffiliations(affiliations);
        setIsLoading(false);
        setInitialized(true);
      },
      (err) => {
        console.error('❌ usePermissions - Erreur listener:', err);
        setError(err.message);
        setIsLoading(false);
      }
    );

    return () => {
      console.log(`🧹 usePermissions - Suppression du listener pour customerId: ${customerId}`);
      unsubscribe();
    };
  }, [customerId, currentUser?.uid]);

  /**
   * Recharge les permissions (legacy, maintenant géré par le listener)
   */
  const refreshPermissions = useCallback(async () => {
    // Le listener s'en occupe automatiquement, mais on garde la fonction pour la compatibilité
    console.log('🔄 usePermissions - refreshPermissions appelé (le listener s\'en chargera)');
  }, []);

  /**
   * Vérifie si l'utilisateur est admin pour une organisation
   */
  const isAdminForOrganization = useCallback((organizationId: string): boolean => {
    // Les super admins ont accès à tout, quelle que soit l'organisation
    if (isSuperAdmin) return true;
    if (!currentUser?.uid) return false;

    // Les affiliations sont déjà filtrées par customerId de l'utilisateur connecté
    const affiliation = userAffiliations.find(aff =>
      aff.organizationId === organizationId &&
      aff.status === 'active'
    );

    return affiliation ? (affiliation.role === 'admin' || affiliation.role === 'owner') : false;
  }, [currentUser?.uid, userAffiliations, isSuperAdmin]);

  /**
   * Vérifie si l'utilisateur a accès à une organisation
   */
  const hasAccessToOrganization = useCallback((organizationId: string): boolean => {
    // Super admin: accès total à toutes les organisations
    if (isSuperAdmin) return true;
    if (!currentUser?.uid) return false;

    // Les affiliations sont déjà filtrées par customerId de l'utilisateur connecté
    return userAffiliations.some(affiliation =>
      affiliation.organizationId === organizationId &&
      affiliation.status === 'active'
    );
  }, [currentUser?.uid, userAffiliations, isSuperAdmin]);

  /**
   * Récupère le rôle de l'utilisateur pour une organisation
   */
  const getUserRole = useCallback((organizationId: string): UserAffiliation['role'] | null => {
    if (isSuperAdmin) return 'owner';
    if (!currentUser?.uid) return null;

    // Les affiliations sont déjà filtrées par customerId de l'utilisateur connecté
    const affiliation = userAffiliations.find(aff =>
      aff.organizationId === organizationId &&
      aff.status === 'active'
    );

    return affiliation ? affiliation.role : null;
  }, [currentUser?.uid, userAffiliations, isSuperAdmin]);

  /**
   * Récupère l'affiliation active pour une organisation
   */
  const getAffiliation = useCallback((organizationId: string): UserAffiliation | null => {
    if (isSuperAdmin) {
      // On ne retourne pas d'affiliation réelle, mais les méthodes en aval court-circuitent déjà pour super admin.
      return null;
    }
    if (!currentUser?.uid) {
      console.log('🔐 usePermissions - getAffiliation: pas de currentUser');
      return null;
    }

    // Les affiliations sont déjà filtrées par customerId de l'utilisateur connecté
    const affiliation = userAffiliations.find(aff =>
      aff.organizationId === organizationId &&
      aff.status === 'active'
    ) || null;

    // Debug: Log pour comprendre pourquoi l'affiliation n'est pas trouvée
    console.log('🔐 usePermissions - getAffiliation:', {
      organizationId,
      userAffiliationsCount: userAffiliations.length,
      foundAffiliation: !!affiliation,
      affiliationId: affiliation?.id,
      affiliationRole: affiliation?.role,
      affiliationStatus: affiliation?.status,
      affiliationPermissions: affiliation?.permissions,
      canManageConfiguration: affiliation?.permissions?.canManageConfiguration,
      // Utiliser directement userAffiliations sans mapper
      allAffiliations: userAffiliations
    });

    return affiliation;
  }, [currentUser?.uid, userAffiliations, isSuperAdmin]);

  /**
   * Vérifie si l'utilisateur peut voir les commandes
   */
  const canViewOrders = useCallback((organizationId: string): boolean => {
    if (isSuperAdmin) return true;
    const affiliation = getAffiliation(organizationId);
    return affiliation?.permissions?.canViewOrders ?? false;
  }, [getAffiliation, isSuperAdmin]);

  /**
   * Vérifie si l'utilisateur peut gérer les commandes
   */
  const canManageOrders = useCallback((organizationId: string): boolean => {
    if (isSuperAdmin) return true;
    const affiliation = getAffiliation(organizationId);
    return affiliation?.permissions?.canManageOrders ?? false;
  }, [getAffiliation, isSuperAdmin]);

  /**
   * Vérifie si l'utilisateur peut gérer la configuration
   */
  const canManageConfiguration = useCallback((organizationId: string): boolean => {
    if (isSuperAdmin) return true;

    const affiliation = getAffiliation(organizationId);

    if (!affiliation) {
      return false;
    }

    // console.log('✅ usePermissions - access check:', { hasPermission: affiliation.permissions?.canManageConfiguration });
    return affiliation.permissions?.canManageConfiguration ?? false;
  }, [getAffiliation, isSuperAdmin]);

  /**
   * Vérifie si l'utilisateur peut voir les analytics
   */
  const canViewAnalytics = useCallback((organizationId: string): boolean => {
    if (isSuperAdmin) return true;
    const affiliation = getAffiliation(organizationId);
    return affiliation?.permissions?.canViewAnalytics ?? false;
  }, [getAffiliation, isSuperAdmin]);

  /**
   * Vérifie si l'utilisateur peut gérer les utilisateurs
   */
  const canManageUsers = useCallback((organizationId: string): boolean => {
    if (isSuperAdmin) return true;
    const affiliation = getAffiliation(organizationId);
    return affiliation?.permissions?.canManageUsers ?? false;
  }, [getAffiliation, isSuperAdmin]);

  return {
    isLoading,
    initialized,
    error,
    isAdminForOrganization,
    hasAccessToOrganization,
    getUserRole,
    canViewOrders,
    canManageOrders,
    canManageConfiguration,
    canViewAnalytics,
    canManageUsers,
    userAffiliations,
    refreshPermissions,
  };
};
